import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtNum } from "../api";
import { useResource } from "../hooks";
import AskOIM from "../components/AskOIM";
import { Play, Pause, Square, RotateCcw } from "lucide-react";

const SPEEDS = [1, 5, 10, 50];

export default function SimulationStudio({ state, refresh }) {
  const catalog = useResource(() => api.catalog(), [], 60000);
  const scenarios = useResource(() => api.scenarios(), [], 60000);
  const [selected, setSelected] = useState("catalyst_degradation");
  const [severity, setSeverity] = useState(55);
  const [busy, setBusy] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);

  const active = state?.anomalies || [];
  const cat = catalog?.catalog || [];
  const presets = scenarios?.scenarios || [];

  const inject = async () => {
    setBusy(true);
    try { await api.inject(selected, severity); setActiveScenario(null); refresh && refresh(); }
    finally { setBusy(false); }
  };
  const clearAll = async () => {
    setBusy(true);
    try { await api.clearAll(); setActiveScenario(null); refresh && refresh(); }
    finally { setBusy(false); }
  };
  const applyScenario = (key) => async () => {
    setBusy(true);
    try { await api.applyScenario(key); setActiveScenario(key); refresh && refresh(); }
    finally { setBusy(false); }
  };
  const control = (action) => async () => {
    setBusy(true);
    try { await api.control({ action }); refresh && refresh(); }
    finally { setBusy(false); }
  };
  const setSpeed = (speed) => async () => {
    setBusy(true);
    try { await api.control({ speed }); refresh && refresh(); }
    finally { setBusy(false); }
  };

  return (
    <div className="stack" data-testid="simulation-studio">
      <div className="page-title">
        <div>
          <h1>Simulation Studio</h1>
          <div className="sub">Administrator console · Inject anomalies & observe causality</div>
        </div>
        <div className="sub">Speed {state?.speed || 1}× · Tick #{state?.tick || 0}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, alignItems: "stretch" }}>
        {/* LEFT — controls + anomaly panel */}
        <div className="stack">
          {/* Scenario presets */}
          <div className="card" data-testid="scenario-presets">
            <div className="card-header">
              <div className="card-title">Scenario Presets</div>
              <span className="chip">One-click demo</span>
            </div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {presets.map(p => {
                const isActive = activeScenario === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={applyScenario(p.key)}
                    disabled={busy}
                    data-testid={`scenario-${p.key}`}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      background: isActive ? "var(--bg-overlay)" : "var(--bg-base)",
                      border: "1px solid " + (isActive ? "var(--brand-amber)" : "var(--border-soft)"),
                      borderLeft: "3px solid " + (
                        p.key === "healthy" ? "var(--brand-green)" :
                        p.key === "catalyst" ? "var(--brand-amber)" :
                        p.key === "steam_network" ? "#7aa8ff" :
                        "var(--status-critical)"
                      ),
                      borderRadius: 2,
                      cursor: busy ? "not-allowed" : "pointer",
                      transition: "all 140ms ease",
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    <div className="mono" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: isActive ? "var(--brand-amber-soft)" : "var(--text-primary)", fontWeight: 600 }}>{p.label}</div>
                    <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 6, lineHeight: 1.45 }}>{p.description}</div>
                    <div className="mono" style={{ fontSize: 10, color: "var(--text-mute)", marginTop: 8, letterSpacing: "0.06em" }}>
                      {p.anomalies.length === 0 ? "0 anomalies" : `${p.anomalies.length} anomal${p.anomalies.length === 1 ? "y" : "ies"} injected`}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Transport */}
          <div className="card" data-testid="transport-controls">
            <div className="card-header"><div className="card-title">Simulation Controls</div></div>
            <div className="card-body flex" style={{ gap: 18, flexWrap: "wrap" }}>
              <div className="btn-group" data-testid="transport-group">
                <button className={"btn success"} onClick={control("start")} disabled={busy} data-testid="sim-start"><Play size={12} />Start</button>
                <button className="btn" onClick={control("pause")} disabled={busy} data-testid="sim-pause"><Pause size={12} />Pause</button>
                <button className="btn" onClick={control("stop")} disabled={busy} data-testid="sim-stop"><Square size={12} />Stop</button>
                <button className="btn danger" onClick={control("reset")} disabled={busy} data-testid="sim-reset"><RotateCcw size={12} />Reset</button>
              </div>
              <div className="flex" style={{ gap: 8 }}>
                <span className="mono muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Speed</span>
                <div className="btn-group" data-testid="speed-group">
                  {SPEEDS.map(sp => (
                    <button key={sp} className={"btn" + (state?.speed === sp ? " active" : "")} onClick={setSpeed(sp)} disabled={busy} data-testid={`speed-${sp}`}>{sp}×</button>
                  ))}
                </div>
              </div>
              <div className="flex" style={{ gap: 8 }}>
                <span className="mono muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Data Source</span>
                <div className="btn-group">
                  <button className="btn active" data-testid="src-sim">Simulated</button>
                  <button className="btn" disabled title="Live mode reserved for plant deployment" data-testid="src-live">Live</button>
                </div>
              </div>
            </div>
          </div>

          {/* Anomaly panel */}
          <div className="card" data-testid="anomaly-panel">
            <div className="card-header">
              <div className="card-title">Anomaly Engine</div>
              <span className="chip">{active.length} active</span>
            </div>
            <div className="card-body">
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 18 }}>
                <div>
                  <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>Select Anomaly</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
                    {cat.map(a => (
                      <button
                        key={a.key}
                        className={"btn" + (selected === a.key ? " primary" : "")}
                        style={{ justifyContent: "flex-start", textAlign: "left", padding: "8px 10px", textTransform: "none", letterSpacing: 0, fontSize: 11.5 }}
                        onClick={() => setSelected(a.key)}
                        data-testid={`anomaly-opt-${a.key}`}
                      >
                        <span style={{ width: 14 }} className={`chip ${a.category}`} />
                        <span style={{ marginLeft: 6 }}>{a.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>Severity</div>
                  <div className="mono" style={{ fontSize: 38, fontWeight: 700, color: severity > 70 ? "var(--status-critical)" : severity > 35 ? "var(--brand-amber-soft)" : "var(--brand-green-soft)" }}>
                    {severity}<span style={{ fontSize: 14, marginLeft: 4, color: "var(--text-tertiary)", fontWeight: 400 }}>%</span>
                  </div>
                  <input
                    type="range" min="0" max="100" value={severity}
                    onChange={e => setSeverity(parseInt(e.target.value))}
                    className="severity-slider"
                    data-testid="severity-slider"
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <button className="btn amber" onClick={inject} disabled={busy} data-testid="inject-btn" style={{ flex: 1 }}>
                      Inject Anomaly
                    </button>
                    <button className="btn danger" onClick={clearAll} disabled={busy} data-testid="clear-all-btn">
                      Clear All
                    </button>
                  </div>
                  <div className="mono" style={{ marginTop: 10, fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
                    {cat.find(a => a.key === selected)?.description}
                  </div>
                </div>
              </div>

              {/* Active list */}
              {active.length > 0 && (
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border-soft)" }}>
                  <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 10 }}>Active Anomalies</div>
                  <div className="stack" style={{ gap: 6 }}>
                    {active.map(a => (
                      <div key={a.key} className="flex-between" style={{ padding: "8px 12px", background: "var(--bg-raised)", border: "1px solid var(--border-soft)" }}>
                        <div className="flex" style={{ gap: 10 }}>
                          <span className={`chip ${a.category}`}>{a.category}</span>
                          <span style={{ fontSize: 13 }}>{a.label}</span>
                        </div>
                        <span className="mono" style={{ fontSize: 11, color: a.severity > 70 ? "var(--status-critical)" : "var(--brand-amber)" }}>SEV {a.severity}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Immediate effects */}
          <div className="card" data-testid="immediate-effects">
            <div className="card-header"><div className="card-title">Immediate Effects (Live)</div></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
              <EffectCell label="Yield" value={fmtNum(state?.kpis?.yield_pct, 2)} unit="%" />
              <EffectCell label="H₂ Intensity" value={fmtNum(state?.kpis?.h2_intensity, 1)} unit="kg/t" />
              <EffectCell label="PCF" value={fmtNum(state?.kpis?.pcf, 2)} unit="kg/kg" />
              <EffectCell label="Margin / hr" value={fmtINR(state?.economics?.margin_inrh)} unit="" economic />
              <EffectCell label="Leakage / hr" value={fmtINR(state?.economics?.leakage_inrh)} unit="" amber />
              <EffectCell label="Scope-1" value={fmtNum(state?.carbon?.scope1_kgh, 0)} unit="kg/h" />
              <EffectCell label="Scope-2" value={fmtNum(state?.carbon?.scope2_kgh, 0)} unit="kg/h" />
              <EffectCell label="Anomalies" value={active.length} unit="" />
            </div>
          </div>
        </div>

        {/* RIGHT — persistent AI Advisor side panel */}
        <SidePanelAI selected={selected} active={active} severity={severity} />
      </div>
    </div>
  );
}

function EffectCell({ label, value, unit, economic, amber }) {
  return (
    <div style={{ background: "var(--bg-base)", border: "1px solid var(--border-soft)", padding: "10px 12px", borderRadius: 2 }}>
      <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase" }}>{label}</div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 6, color: amber ? "var(--brand-amber-soft)" : economic ? "var(--brand-green-soft)" : "var(--text-primary)" }}>
        {value}<span style={{ fontSize: 10, marginLeft: 4, color: "var(--text-tertiary)" }}>{unit}</span>
      </div>
    </div>
  );
}

function SidePanelAI({ selected, active, severity }) {
  return (
    <div className="side-panel" data-testid="sim-side-ai">
      <div className="sp-head">✨ OIM AI Advisor</div>
      <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
        <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Quick Diagnosis</div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text-primary)" }}>
          {active.length === 0
            ? "Plant operating within nominal envelope. Use the panel left to inject an anomaly and watch the causal chain propagate."
            : `${active.length} active anomaly${active.length === 1 ? "" : "ies"}. Primary: ${active[0].label} at ${active[0].severity}% severity. Expect ${active[0].target} performance to deviate from benchmark.`}
        </div>

        <div style={{ marginTop: 18 }}>
          <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Suggested Question</div>
          <AskOIM question="Walk me through the causal chain: why has profitability dropped today?" source="simulation_studio" label="Open Full Advisor" />
        </div>

        <div style={{ marginTop: 18 }}>
          <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Demo Sequence</div>
          <ol style={{ paddingLeft: 18, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.7 }}>
            <li>Select <span className="mono" style={{ color: "var(--brand-amber)" }}>Catalyst Degradation</span></li>
            <li>Drag severity to <span className="mono" style={{ color: "var(--brand-amber)" }}>65%</span></li>
            <li>Click <span className="mono" style={{ color: "var(--brand-amber)" }}>Inject</span></li>
            <li>Open Plant Value Center → see leakage rise</li>
            <li>Open AI Advisor → ask "Why did profitability drop?"</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
